import React, { Component, ErrorInfo, PropsWithChildren } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/pro-light-svg-icons';
import { SizeProp } from '@fortawesome/fontawesome-svg-core';
import Text from '@/presentation/text';
import { ThemeContext } from 'styled-components';
import SErrorBoundary from './ErrorBoundary.styled';

interface Props {
  size?: 'xs' | 's' | 'm';
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<PropsWithChildren<Props>, State> {
  static defaultProps = {
    size: 's',
  };

  static contextType = ThemeContext;

  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error, errorInfo: ErrorInfo): State {
    return { hasError: true, error, errorInfo };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    this.setState({ error, errorInfo });
  }

  getIconSize(): SizeProp {
    const { size } = this.props;

    switch (size) {
      case 'm':
        return '5x';
      case 'xs':
        return '1x';
      default:
        return '2x';
    }
  }

  public render() {
    const { size } = this.props;

    if (this.state.hasError) {
      return (
        <SErrorBoundary className={this.props.size}>
          <FontAwesomeIcon icon={faTriangleExclamation} size={this.getIconSize()} />
          <div>
            <Text size={size}>Sorry, there was an error.</Text>
            <details className="mt-10">
              <summary>technical details</summary>
              <Text size={size}>{this.state.error && this.state.error.toString()}</Text>
              <Text size="xxs" className="mt-15">
                {this.state.errorInfo && this.state.errorInfo.componentStack}
              </Text>
            </details>
          </div>
        </SErrorBoundary>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
